/**
 * @file Transformations.js
 * @description Provides transformation functions for non-linear parameter mappings.
 * Each transformation includes an `inputTransform` and an `outputTransform`.
 * 
 * @version 2.0.0
 * @autor 𝐵𝓇𝓊𝓃𝒶 𝒢𝓊𝒶𝓇𝓃𝒾𝑒𝓇𝒾
 * @license MIT
 * @date 2024-12-08
 * @example
 * // Example of using the linear transformation with ParameterManager
 * import { linear } from './Transformations.js';
 * import { ParameterManager } from './ParameterManager.js';
 * 
 * const paramManager = new ParameterManager();
 * 
 * paramManager.addParameter(
 *   'volume',
 *   0.5, // Initial normalized value
 *   0,   // Min raw value
 *   100, // Max raw value
 *   true, // isBidirectional
 *   linear.inverse, // inputTransform
 *   linear.forward // outputTransform
 * );
 */

/**
 * @typedef {Object} Transformation
 * @property {function(number): number} forward - Transforms a raw value to a normalized value.
 * @property {function(number): number} inverse - Transforms a normalized value back to a raw value.
 */

/**
 * Linear transformation (identity).
 * Suitable for parameters where linear scaling is appropriate.
 * 
 * @type {Transformation}
 * @memberof CoreModule 

 * @property {function(number): number} forward - Returns the input value unchanged.
 * @property {function(number): number} inverse - Returns the input value unchanged.
 */
export const linear = {
  forward: (x) => x,
  inverse: (normalized) => normalized,
};
